import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Slider, IconButton, FormControlLabel } from '@mui/material';
import { Save, Close, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useGetItemQuery, useUpdatePropertyMutation } from '../../../store/docApi';
import CanvasEdit from './CanvasEdit';
import CanvasView from './CanvasView';
import TopMenuBar from './TopMenuBar';
import ColorPickerPanel from './ColorPickerPanel';
import StitchPickerPanel from './StitchPickerPanel';
import GridPanel from './GridPanel';
import EditPanel from './EditPanel';
import SettingsPanel from './SettingsPanel';
import MotifPlacementOverlay from './MotifPlacementOverlay';
import MotifPlacementPanel from './MotifPlacementPanel';
import ShapesPanel from './ShapesPanel';
import ChartDataProvider, { useChartData } from './ChartDataProvider';

// Internal component that uses the chart data context
function ChartEditorContent() {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const params = useParams();

  // Get chart data from context
  const {
    settings,
    hasChanges,
    motifForPlacement,
    motifPlacementPosition,
    motifColorSource,
    colors,
    data,
    knitdata,
    actions
  } = useChartData();

  // Local UI state
  const [showPreview, setShowPreview] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'colors', 'stitches', 'grid', 'edit', or null
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [previousPanelState, setPreviousPanelState] = useState(null);

  // Close other panels when motif placement is active and save previous state
  useEffect(() => {
    if (motifForPlacement && !previousPanelState) {
      // Save current panel state when motif placement starts
      const currentState = {
        activePanel,
        isPanelCollapsed
      };
      setPreviousPanelState(currentState);

      // Close panel (motif placement panel will be shown instead)
      setActivePanel(null);
      setIsPanelCollapsed(false);
    }
  }, [motifForPlacement, previousPanelState, activePanel, isPanelCollapsed]);

  // Restore previous panel state when motif placement ends
  useEffect(() => {
    if (!motifForPlacement && previousPanelState) {
      // Restore previous panel state
      setActivePanel(previousPanelState.activePanel);
      setIsPanelCollapsed(previousPanelState.isPanelCollapsed);
      setPreviousPanelState(null);
    }
  }, [motifForPlacement, previousPanelState]);

  const handleClose = () => {
    if (hasChanges) {
      setShowCloseConfirmDialog(true);
    } else {
      navigate(`/workspaces/${params.pk}/${params.id}/motifs/${params.subId}`);
    }
  };

  const handleCloseWithoutSaving = () => {
    setShowCloseConfirmDialog(false);
    navigate(`/workspaces/${params.pk}/${params.id}/motifs/${params.subId}`);
  };

  const handleSaveAndClose = () => {
    actions.saveData();
    setShowCloseConfirmDialog(false);
    navigate(`/workspaces/${params.pk}/${params.id}/motifs/${params.subId}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Close />}
          onClick={handleClose}
        >
          Close
        </Button>
        <Button
          variant="outlined"
          startIcon={<Save />}
          onClick={actions.saveData}
          disabled={!hasChanges}
        >
          Save
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={showPreview}
              onChange={() => setShowPreview(!showPreview)}
              size="small"
            />
          }
          label="Show Preview"
        />
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top Menu Bar */}
        {!showPreview && <TopMenuBar canvasRef={canvasRef} activePanel={activePanel} onPanelToggle={setActivePanel} />}

        {/* Main content area with chart */}
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          {/* Sidebar Panel - Colors, Stitches, Grid, Edit, or Motif Placement (mutually exclusive) */}
          {((activePanel || motifForPlacement) && !showPreview) && (
            <Box sx={{
              width: isPanelCollapsed ? '30px' : '250px',
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              height: 'fit-content',
              maxHeight: '100%',
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            }}>
              {/* Panel Header - Always Visible */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isPanelCollapsed ? 'center' : 'space-between',
                p: 1,
                borderBottom: isPanelCollapsed ? 'none' : '1px solid',
                borderColor: 'divider',
                minHeight: '40px'
              }}>
                {!isPanelCollapsed && (
                  <Typography variant="h6" sx={{ flexShrink: 0 }}>
                    {activePanel === 'colors' && 'Colors'}
                    {activePanel === 'stitches' && 'Stitches'}
                    {activePanel === 'shapes' && 'Shapes'}
                    {activePanel === 'grid' && 'Grid'}
                    {activePanel === 'edit' && 'Edit'}
                    {activePanel === 'settings' && 'Settings'}
                    {motifForPlacement && 'Place Motif'}
                  </Typography>
                )}
                {!motifForPlacement && (
                  <IconButton
                    size="small"
                    onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                    sx={{
                      transform: isPanelCollapsed ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    {isPanelCollapsed ? <ChevronRight /> : <ChevronLeft />}
                  </IconButton>
                )}
              </Box>

              {/* Panel Content - Hidden When Collapsed */}
              {!isPanelCollapsed && (
                <Box sx={{ flex: 1, minHeight: 0, p: motifForPlacement ? 0 : 1 }}>
                  {activePanel === 'colors' && <ColorPickerPanel />}
                  {activePanel === 'stitches' && <StitchPickerPanel />}
                  {activePanel === 'shapes' && <ShapesPanel />}
                  {activePanel === 'grid' && <GridPanel />}
                  {activePanel === 'edit' && <EditPanel />}
                  {activePanel === 'settings' && <SettingsPanel />}
                  {motifForPlacement && <MotifPlacementPanel />}
                </Box>
              )}

              {/* Collapsed State Indicator */}
              {isPanelCollapsed && !motifForPlacement && (
                <Box sx={{
                  writingMode: 'vertical-rl',
                  textAlign: 'center',
                  py: 2,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="caption" sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    transform: 'rotate(180deg)'
                  }}>
                    {activePanel === 'colors' && 'Colors'}
                    {activePanel === 'stitches' && 'Stitches'}
                    {activePanel === 'shapes' && 'Shapes'}
                    {activePanel === 'grid' && 'Grid'}
                    {activePanel === 'edit' && 'Edit'}
                    {activePanel === 'settings' && 'Settings'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Chart Area - Full Width */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Scale Slider */}
            <Box sx={{ mb: 1, px: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={settings.cellSize}
                onChange={(event, newValue) => {
                  actions.updateSettings({ cellSize: newValue });
                }}
                min={2}
                max={32}
                step={2}
                size='small'
                marks
                sx={{ width: '300px' }}
              />
              <Typography variant="caption" sx={{ minWidth: '60px' }}>
                {settings.cellSize}px
              </Typography>
            </Box>

            {/* Chart Canvas Container */}
            <Box sx={{ position: 'relative', flex: 1 }}>
              {/* Chart Canvas */}
              {showPreview ? (
                <CanvasView canvasRef={canvasRef} />
              ) : (
                <CanvasEdit canvasRef={canvasRef} />
              )}

              {/* Motif Placement Overlay */}
              {motifForPlacement && (
                <MotifPlacementOverlay
                  motif={motifForPlacement}
                  placement={motifPlacementPosition}
                  onPlacementChange={actions.setMotifPlacementPosition}
                  cellSize={settings.cellSize}
                  colorSource={motifColorSource}
                  chartColors={colors}
                  chartSettings={settings}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Debug Data Display */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
        <Box sx={{
          flex: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: '500px',
          overflow: 'auto'
        }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Data Array</Typography>
          <Box component="pre" sx={{
            fontSize: '10px',
            fontFamily: 'monospace',
            margin: 0,
            whiteSpace: 'pre-wrap'
          }}>
            {data && Array.isArray(data) ? data.map((row, i) =>
              `Row ${i}: [${Array.isArray(row) ? row.map(cell => cell === null ? 'null' : cell === undefined ? 'undefined' : cell).join(', ') : row}]`
            ).join('\n') : JSON.stringify(data, null, 2)}
          </Box>
        </Box>
        <Box sx={{
          flex: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: '500px',
          overflow: 'auto'
        }}>
          <Typography variant="h6" sx={{ mb: 1 }}>KnitData Array</Typography>
          <Box component="pre" sx={{
            fontSize: '10px',
            fontFamily: 'monospace',
            margin: 0,
            whiteSpace: 'pre-wrap'
          }}>
            {knitdata && Array.isArray(knitdata) ? knitdata.map((row, i) =>
              `Row ${i}: [${Array.isArray(row) ? row.map(cell => cell === null ? 'null' : cell === undefined ? 'undefined' : cell === '' ? '""' : cell).join(', ') : row}]`
            ).join('\n') : JSON.stringify(knitdata, null, 2)}
          </Box>
        </Box>
      </Box>

      {/* Close Confirmation Dialog */}
      <Dialog open={showCloseConfirmDialog} onClose={() => setShowCloseConfirmDialog(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Do you want to save before closing?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWithoutSaving} color="secondary">
            Don't Save
          </Button>
          <Button onClick={() => setShowCloseConfirmDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveAndClose} variant="contained">
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Main component that provides the chart data context
export default function ChartEditor() {
  const params = useParams();
  const { data: item } = useGetItemQuery({ label: 'workspace', pk: params.pk, id: params.id });
  const [updateProperty] = useUpdatePropertyMutation();

  const motif = item?.motifs?.[params.subId];

  function onSaveProperty(name, value) {
    updateProperty({
      label: item.label,
      pk: item.pk,
      id: item.id,
      name: `motifs/${params.subId}/${name}`,
      value
    });
  }

  if (!item || !motif) {
    return null;
  }

  return (
    <ChartDataProvider motif={motif} item={item} onSaveProperty={onSaveProperty}>
      <ChartEditorContent />
    </ChartDataProvider>
  );
}
